import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ClientSubscriptionStatus = 'active' | 'trial' | 'expired';

// One row = one Admin and that Admin's current subscription
export interface ClientSubscriptionItem {
  // Latest paid order id; null for trial and never-paid rows
  subscription_id: string | null;
  client_name: string | null;
  // Unique, used as the row key
  phone: string;
  post: string | null;
  village: string | null;
  samiti: string | null;
  district: string | null;
  state: string | null;
  plan_name: string | null;
  billing_cycle: string;
  // In paise; null for trial and never-paid rows
  amount_paise: number | null;
  payment_gateway: string | null;
  started_at: string | null;
  expiry_date: string | null;
  status: ClientSubscriptionStatus;
  is_blocked: boolean;
}

export interface SubscriptionCounts {
  all: number;
  active: number;
  trial: number;
  expired: number;
}

export interface ClientSubscriptionState {
  items: ClientSubscriptionItem[];
  // Tab badges: do not change with the status filter or search
  counts: SubscriptionCounts;
  // Rows matching the current filter + search, used for paging
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
}

const initialState: ClientSubscriptionState = {
  items: [],
  counts: { all: 0, active: 0, trial: 0, expired: 0 },
  total: 0,
  limit: 25,
  offset: 0,
  loading: false,
  error: null,
};

const clientSubscriptionSlice = createSlice({
  name: 'clientSubscriptions',
  initialState,
  reducers: {
    setClientSubscriptionLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setClientSubscriptionError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setClientSubscriptionPage: (
      state,
      action: PayloadAction<{
        items: ClientSubscriptionItem[];
        counts: SubscriptionCounts;
        total: number;
        limit: number;
        offset: number;
      }>
    ) => {
      const { items, counts, total, limit, offset } = action.payload;
      state.items = items;
      state.counts = counts;
      state.total = total;
      state.limit = limit;
      state.offset = offset;
    },
  },
});

export const { setClientSubscriptionLoading, setClientSubscriptionError, setClientSubscriptionPage } =
  clientSubscriptionSlice.actions;

export default clientSubscriptionSlice.reducer;
